import axios from "axios";
import { AUTH_REFRESH } from "@/api/Endpoints/auth";
import config from "../config";

const baseUrl = config.apiBaseUrl;

export const AxiosInstance = axios.create({
	baseURL: baseUrl,
	timeout: 30000,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
		Accept: "application/json",
	},
	paramsSerializer: {
		encode: (params: string) => encodeURIComponent(params),
	},
});

// Request interceptor to add the JWT access token
AxiosInstance.interceptors.request.use(
	(config) => {
		const accessToken = localStorage.getItem("accessToken");

		if (accessToken && accessToken !== "undefined") {
			config.headers["Authorization"] = `Bearer ${accessToken}`;
		}

		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

let isRefreshing = false;
let failedQueue: Array<{
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
	console.log("Processing queued requests:", failedQueue.length);
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token as string);
		}
	});
	failedQueue = [];
};

// Response interceptor for handling token refresh on 401 errors
AxiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		const refreshUrl = `${baseUrl}/${AUTH_REFRESH}`;

		if (
			error.response?.status === 401 &&
			!originalRequest._retry &&
			originalRequest.url !== refreshUrl
		) {
			if (isRefreshing) {
				console.log(
					"Token expired, adding request to queue:",
					originalRequest.url,
				);
				return new Promise(function (resolve, reject) {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers["Authorization"] = "Bearer " + token;
						return AxiosInstance(originalRequest);
					})
					.catch((err) => {
						return Promise.reject(err);
					});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			console.log("Trying to refresh token...");

			const refreshToken = localStorage.getItem("refreshToken");

			if (refreshToken) {
				try {
					console.log("Calling refresh endpoint...");
					const refreshResponse = await axios.post(refreshUrl, {
						refresh: refreshToken,
					});

					console.log("Refresh successful");

					const newAccessToken = refreshResponse.data.access;
					const newRefreshToken = refreshResponse.data.refresh;

					localStorage.setItem("accessToken", newAccessToken);
					localStorage.setItem("refreshToken", newRefreshToken);

					AxiosInstance.defaults.headers["Authorization"] =
						`Bearer ${newAccessToken}`;
					originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

					processQueue(null, newAccessToken);
					isRefreshing = false;

					return AxiosInstance(originalRequest);
				} catch (refreshError) {
					processQueue(refreshError, null);
					isRefreshing = false;

					console.error("Unable to refresh token", refreshError);
					localStorage.removeItem("accessToken");
					localStorage.removeItem("refreshToken");
					delete AxiosInstance.defaults.headers["Authorization"];
					return Promise.reject(refreshError);
				}
			} else {
				isRefreshing = false;

				console.error("No refresh token available");
				localStorage.removeItem("accessToken");
				delete AxiosInstance.defaults.headers["Authorization"];
				return Promise.reject(error);
			}
		}

		return Promise.reject(error);
	},
);

export default AxiosInstance;
